import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { PRIORITY_OPTIONS } from '../lib/priority';

/**
 * Two-step quick "Create Work Order" flow, launched from the header gear menu.
 *   Step 1: Location → Equipment picker (mirrors QuickMaintenanceDialog).
 *   Step 2: Standard work-order form (title, description, priority, due date,
 *           frequency, reminders, notes).
 */
const blankForm = {
  title: '',
  description: '',
  status: 'not_started',
  priority: '',
  due_date: '',
  assigned_to: '',
  notes: '',
  frequency_days: '',
  reminder_enabled: false,
};

const QuickWorkOrderDialog = ({ open, onClose, onSuccess }) => {
  const [step, setStep] = useState(1);

  const [locations, setLocations] = useState([]);
  const [machinery, setMachinery] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState('');
  const [selectedMachineryId, setSelectedMachineryId] = useState('');
  const [loadingData, setLoadingData] = useState(false);

  const [formData, setFormData] = useState(blankForm);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStep(1);
    setSelectedLocationId('');
    setSelectedMachineryId('');
    setMachinery([]);
    setFormData(blankForm);
    (async () => {
      try {
        const res = await axios.get(`${API}/locations`);
        setLocations(res.data);
      } catch (e) {
        toast.error('Failed to load locations');
      }
    })();
  }, [open]);

  useEffect(() => {
    if (!selectedLocationId) {
      setMachinery([]);
      return;
    }
    setLoadingData(true);
    (async () => {
      try {
        const res = selectedLocationId === 'unassigned'
          ? await axios.get(`${API}/machinery/unassigned`)
          : await axios.get(`${API}/locations/${selectedLocationId}/machinery`);
        // Alphabetize by component name for quick scanning.
        const sorted = [...res.data].sort((a, b) =>
          (a.component || '').localeCompare(b.component || '')
        );
        setMachinery(sorted);
      } catch (e) {
        toast.error('Failed to load equipment');
      } finally {
        setLoadingData(false);
      }
    })();
    setSelectedMachineryId('');
  }, [selectedLocationId]);

  const selectedMachinery = machinery.find((m) => m.id === selectedMachineryId);

  const handleContinue = () => {
    if (!selectedMachineryId) {
      toast.error('Select equipment first');
      return;
    }
    setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      let dueDateISO = null;
      if (formData.due_date) {
        const [y, m, d] = formData.due_date.split('-').map(Number);
        dueDateISO = new Date(y, m - 1, d, 23, 59, 59).toISOString();
      }
      const payload = {
        machinery_id: selectedMachineryId,
        title: formData.title,
        description: formData.description || null,
        status: formData.status,
        priority: formData.priority || null,
        due_date: dueDateISO,
        assigned_to: formData.assigned_to || null,
        notes: formData.notes || null,
        frequency_days: formData.frequency_days ? parseInt(formData.frequency_days, 10) : null,
        reminder_enabled: !!formData.reminder_enabled,
      };
      const res = await axios.post(`${API}/work-orders`, payload);
      toast.success(`Work order ${res.data.wo_number || ''} created`);
      onSuccess?.();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create work order');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {step === 1 ? 'Quick Create Work Order' : 'Create Work Order'}
          </DialogTitle>
          <DialogDescription>
            {step === 1
              ? 'Select the equipment this work order is for'
              : `For: ${selectedMachinery?.component || ''}`}
          </DialogDescription>
        </DialogHeader>

        {step === 1 ? (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="qwo-location">Plant Location *</Label>
              <select
                id="qwo-location"
                data-testid="quick-wo-location-select"
                value={selectedLocationId}
                onChange={(e) => setSelectedLocationId(e.target.value)}
                className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring"
              >
                <option value="">Select location...</option>
                {locations.map((loc) => (
                  <option key={loc.id} value={loc.id}>{loc.name}</option>
                ))}
                <option value="unassigned">Unassigned Equipment</option>
              </select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="qwo-equipment">Equipment *</Label>
              <select
                id="qwo-equipment"
                data-testid="quick-wo-equipment-select"
                value={selectedMachineryId}
                onChange={(e) => setSelectedMachineryId(e.target.value)}
                disabled={!selectedLocationId || loadingData}
                className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-50"
              >
                <option value="">
                  {loadingData ? 'Loading...' : 'Select equipment...'}
                </option>
                {machinery.map((m) => (
                  <option key={m.id} value={m.id}>{m.component}</option>
                ))}
              </select>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
              <Button
                type="button"
                onClick={handleContinue}
                disabled={!selectedMachineryId}
                data-testid="quick-wo-continue-btn"
              >
                Continue →
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="bg-slate-100 rounded-lg p-3 mb-2">
              <p className="text-sm text-slate-600">
                <strong>Equipment:</strong> {selectedMachinery?.component}
              </p>
              <Button type="button" variant="link" className="p-0 h-auto text-sm" onClick={() => setStep(1)}>
                ← Change selection
              </Button>
            </div>

            <div className="space-y-2">
              <Label htmlFor="qwo-title">Title *</Label>
              <Input
                id="qwo-title"
                data-testid="quick-wo-title-input"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Replace filter on Compressor A"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="qwo-description">Description</Label>
              <Textarea
                id="qwo-description"
                data-testid="quick-wo-description-textarea"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="qwo-status">Status</Label>
                <select
                  id="qwo-status"
                  data-testid="quick-wo-status-select"
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring"
                >
                  <option value="not_started">Not Started</option>
                  <option value="in_progress">In Progress</option>
                  <option value="on_hold">On Hold</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="qwo-priority">Priority</Label>
                <select
                  id="qwo-priority"
                  data-testid="quick-wo-priority-select"
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                  className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring"
                >
                  <option value="">Select priority</option>
                  {PRIORITY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="qwo-due">Due Date</Label>
                <Input
                  id="qwo-due"
                  data-testid="quick-wo-due-date-input"
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="qwo-assigned">Assigned To</Label>
                <Input
                  id="qwo-assigned"
                  data-testid="quick-wo-assigned-input"
                  value={formData.assigned_to}
                  onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}
                  placeholder="Technician name"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 items-end">
              <div className="space-y-2">
                <Label htmlFor="qwo-frequency">Maintenance Frequency (days)</Label>
                <Input
                  id="qwo-frequency"
                  data-testid="quick-wo-frequency-input"
                  type="number"
                  min="1"
                  value={formData.frequency_days}
                  onChange={(e) => setFormData({ ...formData, frequency_days: e.target.value })}
                  placeholder="e.g., 30, 90"
                />
                <p className="text-xs text-slate-500">If set, a fresh WO spawns on completion.</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="qwo-reminder">Enable maintenance reminders</Label>
                <div className="h-9 flex items-center">
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      id="qwo-reminder"
                      data-testid="quick-wo-reminder-toggle"
                      type="checkbox"
                      className="h-4 w-4"
                      checked={!!formData.reminder_enabled}
                      onChange={(e) => setFormData({ ...formData, reminder_enabled: e.target.checked })}
                    />
                    <span className="text-sm text-slate-700">Remind me about this</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="qwo-notes">Notes</Label>
              <Textarea
                id="qwo-notes"
                data-testid="quick-wo-notes-textarea"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
              <Button type="submit" disabled={submitting} data-testid="quick-wo-submit-btn">
                {submitting ? 'Creating...' : 'Create Work Order'}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default QuickWorkOrderDialog;
